/* eslint-disable import/prefer-default-export */
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';
import PlatformUtils from '@appblocks/platforms/PlatformUtils';


export const getCodeBlocks = (feature: BaseFeature): CodeBlock[] => {
    const blocks: Array<CodeBlock> = [];
    const project: ProjectConfiguration = feature.project;

    blocks.push({
        location: 'on_sock_postdata',
        blockType: BlockType.CODE,
        template: `
if sock.httprqstring="" then
    if upload_started=false then
        device_firmware_upload_async(PL_FW_UPG_HTTP, 0)
        upload_started=true
    else
        device_firmware_upload_update()
    end if
else
    sock.getdata(255)
end if
`,
    });

    blocks.push({
        location: 'GLOBAL_DECLARATIONS',
        blockType: BlockType.CODE,
        template: 'declare upgrade_socket_http as byte',
    });

    blocks.push({
        location: 'GLOBAL_DIMS',
        blockType: BlockType.CODE,
        template: 'dim upgrade_socket_http as byte=255',
    });

    blocks.push({
        location: 'GLOBAL_DIMS',
        blockType: BlockType.CODE,
        template: `
dim UPGRADE_URL as string
`,
    });

    blocks.push({
        location: 'GLOBAL_DECLARATIONS',
        blockType: BlockType.CODE,
        template: `
declare UPGRADE_URL as string
`,
    });

    blocks.push({
        location: 'callback_http_content_arrival',
        blockType: BlockType.CODE,
        template: `
if http_request_item.url = UPGRADE_URL then
      upgrade_set_buffer(data)
      dim processing as boolean=true
      dim upgrade_len as byte=len(upgrade_buffer)
      while processing
        device_firmware_upload_update()
        if len(upgrade_buffer)<>upgrade_len then
          processing=true
        else
          processing=false
        end if
        upgrade_len=len(upgrade_buffer)
      wend
end if
            `,
    });

    blocks.push({
        location: 'on_sock_event',
        blockType: BlockType.CODE,
        template: `
if sock.num=upgrade_socket_http AND newstatesimple=PL_SSTS_CLOSED then
    while sock.varlen <> 0
        if upload_started=false then
            device_firmware_upload_async(PL_FW_UPG_HTTP, 0)
            upload_started=true
        else
            device_firmware_upload_update()
        end if
    wend
    upgrade_socket_http=255
end if`,
    });

    blocks.push({
        location: 'DEVICE_TBS',
        blockType: BlockType.CODE,
        template: `
sub on_firmware_update_start(byref current_fw_upg_state as pl_upg_state_t) 
    '****on_firmware_update_start****'
    upload_started=true
end sub

sub on_firmware_update_data_received(byref current_fw_upg_state as pl_upg_state_t) 
    '****on_firmware_update_data_received****'
end sub

sub on_firmware_update_percent_change(byref current_fw_upg_state as pl_upg_state_t) 
    '****on_firmware_update_percent_change****'
    #IFDEF RETARGET_DEBUG_PRINT
    sys.trace(str(current_fw_upg_state.fw_percent)+"%\\r\\n")
    #ELSE
    sys.debugprint(str(current_fw_upg_state.fw_percent)+"%\\r\\n")
    #ENDIF
end sub

sub on_firmware_update_file_complete(byref current_fw_upg_state as pl_upg_state_t)
    '****on_firmware_update_file_complete****'
end sub

sub on_firmware_update_complete(byref current_fw_upg_state as pl_upg_state_t)
    '****on_firmware_update_complete****'
    upload_started=false
    dim i, j as integer
    dim module_type as pl_wln_module_types

        
    if current_fw_upg_state.fw_fail_reason <> PL_FW_FAIL_NONE then
    #IFDEF RETARGET_DEBUG_PRINT
        sys.trace("There was a failure.\\r\\n")
    #ELSE
        sys.debugprint("There was a failure.\\r\\n")
    #ENDIF
    end if

    if current_fw_upg_state.state <> PL_FW_UPG_COMPLETE then
        exit sub
    end if

    pat.play("B~", PL_PAT_CANINT)

    'i = get_firmware_index(WA2000_MON)
    'if i >= 0 then
    '   upgrade_WA2000_firmware(WA2000_MON, i)
    'end if

    i = get_firmware_index(WA2000_APP)

    i =  get_firmware_index(UNCOMPRESSED_TIOS_APP)  
    if i >= 0  then 
    #IFDEF FIRMWARE_UPGRADE_FILENAME
    dim fd_status as pl_fd_status_codes
    dim iii as byte=0
    for iii=0 to 26 - 1
        filenum_release(iii)
    next iii
    fd.filenum=0
    fd.open(FIRMWARE_UPGRADE_FILENAME)
    dim checksum as dword=0
    dim s as string=""
    dim s_bytes(255) as byte
    while fd.pointer < fd.filesize
        s = fd.getdata(255)
        s_bytes=s
        for j = 0 to len(s) - 1
            checksum = checksum + s_bytes(j)
        next i
    wend
    checksum = not checksum
    checksum = checksum + 1
    if current_fw_upg_state.fw_checksums(i) <> checksum then
        sys.debugprint("The firmware update failed.")
        exit sub
    end if
    fd.copyfirmwarefromfile()
    #ELSE
    fd.copyfirmware(current_fw_upg_state.fw_lengths(i)/256+1)
    #ENDIF
    end if

    i=  get_firmware_index(COMPRESSED_TIOS_APP) 
    if i >= 0  then 
        fd.copyfirmwarelzo(YES)
    end if

    i =  get_firmware_index(UNCOMPRESSED_TIOS)  
    if i >= 0  then 
        #IFDEF FIRMWARE_UPGRADE_FILENAME
        fd.open(FIRMWARE_UPGRADE_FILENAME)
        fd.copyfirmwarefromfile()
        #ELSE
        fd.copyfirmware(current_fw_upg_state.fw_lengths(i)/256+1)
        #ENDIF
    end if

    i =  get_firmware_index(TIOS_MON)  
    if i >= 0  then 
        'fd.copymonitor()
    end if

    sys.reboot()
end sub
`,
    });

    blocks.push({
        location: 'callback_http_request_failed',
        blockType: BlockType.CODE,
        template: 'upload_started=false',
    });

    return blocks;
};
